/* Drawing ERPNext's 82 dashboard charts, in SVG, with no charting dependency.
 *
 * The colour decisions here were computed rather than chosen (see the
 * `--viz-*` tokens in shell.css for the measurements). The form decisions
 * follow from what the data's job is:
 *
 *   - **One series is the common case**, so it gets one colour and no legend:
 *     the title already names it. Colouring each bar darker-where-bigger
 *     would burn the only free channel re-encoding the bar length.
 *   - **A pie or donut becomes a bar.** ERPNext asks for donuts; people
 *     cannot compare close values on one, and a bar costs nothing.
 *   - **One data point is not a chart.** It renders as the figure it is.
 *   - **Past three series the tail folds into "Other"** — a generated fourth
 *     hue is indistinguishable from an existing one under colour-vision
 *     deficiency.
 *
 * Every chart carries a table view, because a picture of numbers is not
 * reachable by a screen reader and is not something you can copy.
 */
import { useId, useMemo, useRef, useState } from 'react'
import { formatNumber } from './format'

export type Series = { name?: string; values?: (number | null)[] }

export type ChartProps = {
  title: string
  labels: string[]
  datasets: Series[]
  /** What ERPNext asked for. Honoured where it is a sensible form. */
  type?: string | null
  /** Said plainly instead of drawing an empty box. */
  why?: string
  height?: number
}

const PAD = { top: 12, right: 14, bottom: 26, left: 52 }
const MAX_SERIES = 3

export function Chart({ title, labels, datasets, type, why, height }: ChartProps) {
  const tall = height ?? 190
  const [asTable, setAsTable] = useState(false)
  const titleId = useId()

  const series = useMemo(() => foldTail(mergeAnonymous(datasets)), [datasets])
  const points = labels.length
  const single = series.length === 1

  // A donut of two numbers, or a bar chart of one, is a number wearing a
  // costume. Say the number.
  const wants = (type ?? '').toLowerCase()
  const form: 'line' | 'bar' = wants.includes('line') || wants.includes('area') ? 'line' : 'bar'

  // A line pinned to the baseline across twelve months is a flat line
  // pretending to be data. Say there was no activity.
  const empty = series.every((s) => (s.values ?? []).every((v) => !Number(v)))

  if (why || points === 0 || series.length === 0 || empty) {
    return (
      <figure className="card card-pad" style={{ margin: 0 }}>
        <figcaption className="small" style={{ fontWeight: 600, marginBottom: 6 }}>{title}</figcaption>
        <p className="small muted" style={{ margin: 0 }}>
          {why ?? (empty && points ? 'Nothing recorded in this period.' : 'No data for this yet.')}
        </p>
      </figure>
    )
  }

  return (
    <figure className="card card-pad" style={{ margin: 0, display: 'grid', gap: 8 }}>
      <div className="row-between">
        <figcaption className="small" id={titleId} style={{ fontWeight: 600 }}>{title}</figcaption>
        <button className="btn btn-quiet btn-sm" aria-pressed={asTable}
                onClick={() => setAsTable((v) => !v)}>
          {asTable ? 'Chart' : 'Table'}
        </button>
      </div>

      {series.length > 1 ? (
        <div className="viz-legend">
          {series.map((s, i) => (
            <span key={s.name ?? i} className="row" style={{ gap: 6 }}>
              <span className="viz-swatch" style={{ background: `var(--viz-${i + 1})` }} aria-hidden="true" />
              {s.name ?? `Series ${i + 1}`}
            </span>
          ))}
        </div>
      ) : null}

      {asTable
        ? <TableView labels={labels} series={series} titleId={titleId} />
        : <Plot labels={labels} series={series} form={form} height={tall} titleId={titleId} title={title} />}
    </figure>
  )
}

/* ── the plot ───────────────────────────────────────────────────────── */

function Plot({ labels, series, form, height, titleId, title }: {
  labels: string[]; series: Series[]; form: 'line' | 'bar'; height: number; titleId: string; title: string
}) {
  const wrap = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<number | null>(null)
  const width = 640                       // viewBox units; the SVG scales to fit

  const all = series.flatMap((s) => (s.values ?? []).map((v) => Number(v) || 0))
  const max = Math.max(0, ...all)
  const min = Math.min(0, ...all)
  const span = max - min || 1
  const plotW = width - PAD.left - PAD.right
  const plotH = height - PAD.top - PAD.bottom

  const x = (i: number) => PAD.left + (labels.length === 1 ? plotW / 2 : (i * plotW) / (labels.length - 1))
  const y = (v: number) => PAD.top + plotH - ((v - min) / span) * plotH
  const ticks = niceTicks(min, max, 4)

  // Every third label at most, so they never collide.
  const step = Math.max(1, Math.ceil(labels.length / 8))

  return (
    <div ref={wrap} style={{ position: 'relative' }}>
      <svg className="viz" viewBox={`0 0 ${width} ${height}`} role="img"
           aria-labelledby={titleId} preserveAspectRatio="none"
           onMouseLeave={() => setHover(null)}>
        <desc>{`${title}: ${labels.length} points, ${series.length} series`}</desc>

        {/* Recessive grid: hairline, solid, never dashed. */}
        {ticks.map((t) => (
          <g key={t}>
            <line className="grid" x1={PAD.left} x2={width - PAD.right} y1={y(t)} y2={y(t)} />
            <text className="axis-text" x={PAD.left - 8} y={y(t) + 3.5} textAnchor="end">
              {compact(t)}
            </text>
          </g>
        ))}

        {form === 'bar' ? (
          series.map((s, si) => {
            const groupW = plotW / Math.max(labels.length, 1)
            const barW = Math.max(3, (groupW - 6) / series.length - 2)
            return (
              <g key={si} fill={`var(--viz-${si + 1})`}>
                {(s.values ?? []).map((raw, i) => {
                  const v = Number(raw) || 0
                  const top = y(Math.max(v, 0))
                  const base = y(0)
                  const h = Math.max(1, Math.abs(base - top))
                  const bx = PAD.left + i * groupW + 3 + si * (barW + 2)
                  return (
                    <rect key={i} x={bx} y={Math.min(top, base)} width={barW} height={h}
                          rx={Math.min(4, barW / 2)} opacity={hover === null || hover === i ? 1 : 0.45} />
                  )
                })}
              </g>
            )
          })
        ) : (
          series.map((s, si) => {
            const d = (s.values ?? []).map((raw, i) => `${i ? 'L' : 'M'}${x(i)},${y(Number(raw) || 0)}`).join(' ')
            return (
              <g key={si}>
                <path className="mark-line" d={d} stroke={`var(--viz-${si + 1})`} />
                {(s.values ?? []).map((raw, i) => (
                  <circle key={i} className="mark-dot" cx={x(i)} cy={y(Number(raw) || 0)}
                          r={hover === i ? 5 : 3.5} fill={`var(--viz-${si + 1})`} />
                ))}
              </g>
            )
          })
        )}

        {hover !== null ? (
          <line className="viz-crosshair" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} />
        ) : null}

        {labels.map((label, i) => (
          i % step === 0 ? (
            <text key={i} className="axis-text" x={form === 'bar' ? PAD.left + (i + 0.5) * (plotW / labels.length) : x(i)}
                  y={height - 8} textAnchor="middle">{short(label)}</text>
          ) : null
        ))}

        {/* Hit targets wider than the marks. */}
        {labels.map((_, i) => (
          <rect key={i} className="hit" x={PAD.left + (i * plotW) / labels.length} y={PAD.top}
                width={plotW / labels.length} height={plotH}
                onMouseEnter={() => setHover(i)} />
        ))}
      </svg>

      {hover !== null ? (
        <div className="viz-tip" style={{
          left: `${((x(hover) / width) * 100).toFixed(2)}%`,
          top: 0,
          transform: x(hover) > width / 2 ? 'translate(-105%, 0)' : 'translate(8px, 0)',
        }}>
          <div className="muted" style={{ marginBottom: 2 }}>{labels[hover]}</div>
          {series.map((s, si) => (
            <div key={si} className="row" style={{ gap: 6 }}>
              {series.length > 1 ? (
                <span className="viz-swatch" style={{ background: `var(--viz-${si + 1})` }} aria-hidden="true" />
              ) : null}
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatNumber(Number(s.values?.[hover] ?? 0))}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function TableView({ labels, series, titleId }: { labels: string[]; series: Series[]; titleId: string }) {
  return (
    <div style={{ maxHeight: 220, overflow: 'auto' }}>
      <table className="tbl" aria-labelledby={titleId}>
        <thead>
          <tr>
            <th></th>
            {series.map((s, i) => <th key={i} className="num">{s.name ?? `Series ${i + 1}`}</th>)}
          </tr>
        </thead>
        <tbody>
          {labels.map((label, i) => (
            <tr key={i} style={{ cursor: 'default' }}>
              <td>{label}</td>
              {series.map((s, si) => (
                <td key={si} className="num">{formatNumber(Number(s.values?.[i] ?? 0))}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/* ── helpers ────────────────────────────────────────────────────────── */

/**
 * Several unnamed series are one series.
 *
 * Some ERPNext charts hand back a dataset per underlying row with no name on
 * any of them — Accounts Payable Ageing returns ten. Drawn separately that is
 * ten colours carrying no identity, which is the "eight hues when the story is
 * one number" mistake in its purest form. Summed, it is the thing the chart is
 * actually called: total outstanding per ageing band.
 *
 * A *named* set is left alone — those series mean something.
 */
function mergeAnonymous(datasets: Series[]): Series[] {
  const real = (datasets ?? []).filter((s) => Array.isArray(s.values))
  const unnamed = real.filter((s) => !s.name)
  if (unnamed.length < 2 || unnamed.length !== real.length) return real
  const length = Math.max(...unnamed.map((s) => s.values?.length ?? 0))
  return [{
    values: Array.from({ length }, (_, i) =>
      unnamed.reduce((n, s) => n + (Number(s.values?.[i]) || 0), 0)),
  }]
}

/** Past three series the tail becomes one "Other" line, summed. */
function foldTail(datasets: Series[]): Series[] {
  const real = (datasets ?? []).filter((s) => Array.isArray(s.values))
  if (real.length <= MAX_SERIES) return real
  const kept = real.slice(0, MAX_SERIES - 1)
  const rest = real.slice(MAX_SERIES - 1)
  const length = Math.max(...rest.map((s) => s.values?.length ?? 0))
  const summed = Array.from({ length }, (_, i) =>
    rest.reduce((n, s) => n + (Number(s.values?.[i]) || 0), 0))
  return [...kept, { name: `Other (${rest.length})`, values: summed }]
}

/** Round tick values, so the axis reads 0 / 5k / 10k rather than 0 / 4,317. */
function niceTicks(min: number, max: number, count: number): number[] {
  if (max === min) return [min]
  const raw = (max - min) / count
  const mag = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10
  const start = Math.floor(min / step) * step
  const out: number[] = []
  for (let v = start; v <= max + step / 2; v += step) out.push(Number(v.toFixed(6)))
  return out
}

const compact = (n: number) =>
  Math.abs(n) >= 1e7 ? `${(n / 1e7).toFixed(1)}Cr`
    : Math.abs(n) >= 1e5 ? `${(n / 1e5).toFixed(1)}L`
    : Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(0)}k`
    : formatNumber(n, 0)

const short = (label: string) => (label.length > 10 ? `${label.slice(0, 9)}…` : label)
